import React, { useState, useEffect } from 'react';

const SampleCalculator = ({ onDimensionsChange, onComplete }) => {
  // State for form fields
  const [quiltWidth, setQuiltWidth] = useState(4);
  const [quiltHeight, setQuiltHeight] = useState(4);
  const [tileWidth, setTileWidth] = useState(12);
  const [tileHeight, setTileHeight] = useState(12);
  
  // State for calculation results
  const [calculations, setCalculations] = useState(null);
  const [error, setError] = useState(null);

  // Calculate quilt dimensions whenever inputs change
  useEffect(() => {
    calculateDimensions();
  }, [quiltWidth, quiltHeight, tileWidth, tileHeight]);

  // Function to calculate dimensions
  const calculateDimensions = () => {
    // Clear previous error
    setError(null);

    try {
      // Validate inputs
      if (!quiltWidth || !quiltHeight || !tileWidth || !tileHeight) {
        setCalculations(null);
        return;
      }

      // Convert to numbers
      const qWidth = parseFloat(quiltWidth);
      const qHeight = parseFloat(quiltHeight);
      const tWidth = parseFloat(tileWidth);
      const tHeight = parseFloat(tileHeight);

      // Validate numbers
      if (qWidth <= 0 || qHeight <= 0 || tWidth <= 0 || tHeight <= 0) {
        setError("All dimensions must be positive numbers");
        setCalculations(null);
        return;
      }

      // Calculate grid dimensions
      const columns = Math.floor(qWidth * 12 / tWidth);
      const rows = Math.floor(qHeight * 12 / tHeight);
      
      // Calculate total tiles and coverage
      const totalTiles = columns * rows;
      const coverageWidth = (columns * tWidth) / 12;
      const coverageHeight = (rows * tHeight) / 12;
      
      // Create tile dimensions in pixels for rendering (square tiles)
      const tileWidthPixels = 300; // Fixed size for sample rendering
      const tileHeightPixels = 300; // Fixed size for sample rendering

      // Create result object
      const result = {
        quiltWidth: qWidth,
        quiltHeight: qHeight,
        tileWidth: tWidth,
        tileHeight: tHeight,
        columns: columns,
        rows: rows,
        totalTiles: totalTiles,
        coverageWidth: coverageWidth,
        coverageHeight: coverageHeight,
        tileWidthPixels: tileWidthPixels,
        tileHeightPixels: tileHeightPixels
      };

      // Update state
      setCalculations(result);
      
      // Notify parent component of the change
      if (onDimensionsChange) {
        onDimensionsChange(result);
      }
    } catch (err) {
      setError("Error in calculations. Please check your inputs.");
      setCalculations(null);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Final calculation
    calculateDimensions();
    
    // Mark as completed if we have valid results
    if (calculations && calculations.totalTiles > 0 && onComplete) {
      onComplete(true, calculations);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: "Playfair Display" }}>
        Memory Quilt Size Calculator
      </h2>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Quilt Dimensions */}
          <div>
            <h3 className="text-md font-semibold mb-2">Quilt Dimensions</h3>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Width (ft)
                </label>
                <input
                  type="number"
                  value={quiltWidth}
                  onChange={(e) => setQuiltWidth(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="2"
                  max="10"
                  step="0.5"
                />
              </div>
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Height (ft)
                </label>
                <input
                  type="number"
                  value={quiltHeight}
                  onChange={(e) => setQuiltHeight(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="2"
                  max="10"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          {/* Tile Dimensions */}
          <div>
            <h3 className="text-md font-semibold mb-2">Tile Dimensions</h3>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Width (in)
                </label>
                <input
                  type="number"
                  value={tileWidth}
                  onChange={(e) => setTileWidth(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="4"
                  max="24"
                  step="0.5"
                />
              </div>
              <div className="flex-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Height (in)
                </label>
                <input
                  type="number"
                  value={tileHeight}
                  onChange={(e) => setTileHeight(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  min="4"
                  max="24"
                  step="0.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="mt-6 text-center">
          <button
            type="submit"
            className="px-6 py-2 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-md"
          >
            Calculate Layout
          </button>
        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-2 bg-red-100 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {/* Calculation Results */}
      {calculations && (
        <div className="mt-6 bg-blue-50 p-4 rounded border border-blue-200">
          <h3 className="font-semibold mb-2">Recommended Layout:</h3>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium">Grid Size:</span>
              <span>{calculations.columns}×{calculations.rows}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Total Tiles:</span>
              <span>{calculations.totalTiles}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Coverage:</span>
              <span>{calculations.coverageWidth.toFixed(1)}' × {calculations.coverageHeight.toFixed(1)}'</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Tile Size:</span>
              <span>{calculations.tileWidth}" × {calculations.tileHeight}"</span>
            </div>
          </div>
          
          <p className="text-center mt-4 bg-white p-2 rounded text-md">
            <span className="font-bold">For your demo, please upload <span className="text-indigo-600">{calculations.totalTiles}</span> images.</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default SampleCalculator;
